/**
 * Steady-state convergence detection for the canvas pseudo-transient run loop.
 *
 * ── What this module is (and is NOT) ────────────────────────────────────────
 * This is PURE ORCHESTRATION. It does NOT solve anything, does NOT touch the NS
 * solver internals, the CFL math, convergence tolerances inside the solver, or
 * any thermodynamic formula. It only INSPECTS the metrics the NS solver has
 * ALREADY computed for the current step (`simState.metrics.convergence`) and
 * answers a single question: "has the canvas network marched to steady state?"
 *
 * The canvas run loop in App.tsx marches the existing transient NS step
 * (`stepSimulation`) tick after tick. In steady-state mode we keep stepping
 * (pseudo-transient run-to-convergence) and stop as soon as this helper reports
 * convergence — or bail out when we hit STEADY_STATE_MAX_STEPS.
 *
 * Energy sign convention (project): positive = input. Units: degC, MPa, kJ/kg.
 * None of those appear here — this file reads dimensionless residuals only.
 */

import type { SimulationState } from './types';

/**
 * Residual tolerance for declaring the canvas network "at steady state".
 *
 * The NS solver reports a per-step MASS residual on
 * `simState.metrics.convergence.residual` (dimensionless, mass-imbalance
 * magnitude). When that residual sits at or below this tolerance the network is
 * no longer changing meaningfully between steps and we treat it as steady.
 *
 * NOTE: this is the ORCHESTRATION-LEVEL stop threshold for the run loop, and is
 * deliberately decoupled from the solver's own per-iteration
 * `convergenceTolerance` (which lives inside the NS SIMPLE loop and is NOT
 * touched here). We default it to the same 1e-3 the solver uses as its mass
 * criterion so the loop stops at the same fidelity the solver itself considers
 * converged, but callers can override via `opts.residualTolerance`.
 */
export const STEADY_STATE_RESIDUAL_TOLERANCE = 1e-3;

/**
 * Epsilon for the inter-step "metrics no longer changing" criterion.
 *
 * Even when the raw residual hasn't dipped under the residual tolerance, a
 * network can be effectively at steady state if the key reported metrics
 * (cooling power, generated/consumed power, residual itself) stop changing
 * between consecutive steps. This is a RELATIVE change threshold (fractional):
 * 1e-4 == 0.01% step-over-step change. Kept tight so we never declare steady
 * state on a still-marching solution.
 */
export const STEADY_STATE_METRIC_EPSILON = 1e-4;

/**
 * Hard cap on pseudo-transient iterations before the run loop gives up.
 *
 * At the CFL-limited adaptive step (autoTimeStep semantics) a well-posed
 * network converges in well under this many ticks; the cap exists to stop a
 * non-converging / oscillating / diverging network from spinning forever. When
 * hit, the loop stops and surfaces a "did not reach steady state in N
 * iterations" warning. 5000 ticks at the bumped steady-state tick rate is a few
 * seconds of wall-clock, ample headroom for any convergent canvas network.
 */
export const STEADY_STATE_MAX_STEPS = 5000;

/** Options for {@link isSteadyStateConverged}. */
export interface SteadyStateOptions {
  /** Override the residual stop threshold. Default {@link STEADY_STATE_RESIDUAL_TOLERANCE}. */
  residualTolerance?: number;
  /** Override the inter-step relative-change epsilon. Default {@link STEADY_STATE_METRIC_EPSILON}. */
  metricEpsilon?: number;
  /**
   * Metrics from the PREVIOUS step, used for the inter-step "not changing"
   * criterion. When omitted, only the residual criterion is evaluated.
   * Pass the prior `simState.metrics` so the helper can compare key fields.
   */
  previousMetrics?: SimulationState['metrics'] | null;
}

/**
 * A compact snapshot of the metrics that matter for steady-state detection.
 * Extracted so the inter-step comparison is explicit and testable.
 */
interface SteadyStateMetricSnapshot {
  residual: number;
  totalCoolingPower: number;
  totalPowerGenerated: number;
  totalPowerConsumed: number;
}

function snapshot(metrics: SimulationState['metrics'] | null | undefined): SteadyStateMetricSnapshot | null {
  if (!metrics) return null;
  return {
    residual: metrics.convergence?.residual ?? Number.POSITIVE_INFINITY,
    totalCoolingPower: metrics.totalCoolingPower ?? 0,
    totalPowerGenerated: metrics.totalPowerGenerated ?? 0,
    totalPowerConsumed: metrics.totalPowerConsumed ?? 0,
  };
}

/**
 * Relative change between two values, robust at/near zero.
 * Returns the absolute difference normalised by the larger magnitude; when both
 * are ~0 the change is 0 (no change).
 */
function relativeChange(a: number, b: number): number {
  const denom = Math.max(Math.abs(a), Math.abs(b), 1e-12);
  return Math.abs(a - b) / denom;
}

/**
 * True when the network has reached steady state, judged ENTIRELY from the
 * already-computed metrics on `simState`.
 *
 * Convergence is declared when EITHER:
 *   (a) the solver already reported `isConverged === true` for this step AND the
 *       reported mass residual is finite and at/below the residual tolerance, OR
 *   (b) an inter-step comparison shows the key metrics (residual, cooling power,
 *       power generated/consumed) all changed by less than `metricEpsilon`
 *       relative to the previous step — i.e. the solution has stopped moving.
 *
 * A non-finite residual (diverged / blown-up solve, which the solver reports as
 * POSITIVE_INFINITY) is NEVER treated as converged — neither branch can fire.
 *
 * This function has no side effects and does not mutate its arguments.
 *
 * @param simState  Current simulation state (after the latest step).
 * @param opts      Optional thresholds + previous-step metrics for the
 *                  inter-step criterion.
 */
export function isSteadyStateConverged(
  simState: Pick<SimulationState, 'metrics'>,
  opts: SteadyStateOptions = {},
): boolean {
  const residualTolerance = opts.residualTolerance ?? STEADY_STATE_RESIDUAL_TOLERANCE;
  const metricEpsilon = opts.metricEpsilon ?? STEADY_STATE_METRIC_EPSILON;

  const current = snapshot(simState.metrics);
  if (!current) return false;

  // A diverged / blown-up solve (non-finite residual) is never steady state.
  if (!Number.isFinite(current.residual)) return false;

  // ── Criterion (a): solver-reported convergence at/below residual tolerance ──
  const convergence = simState.metrics?.convergence;
  if (convergence?.isConverged === true && current.residual <= residualTolerance) {
    return true;
  }

  // Residual alone below tolerance also counts as steady (covers cases where the
  // solver's per-step `isConverged` flag lags but the residual is already low).
  if (current.residual <= residualTolerance) {
    return true;
  }

  // ── Criterion (b): inter-step metrics no longer changing ────────────────────
  const previous = snapshot(opts.previousMetrics);
  if (previous && Number.isFinite(previous.residual)) {
    const stable =
      relativeChange(current.residual, previous.residual) < metricEpsilon &&
      relativeChange(current.totalCoolingPower, previous.totalCoolingPower) < metricEpsilon &&
      relativeChange(current.totalPowerGenerated, previous.totalPowerGenerated) < metricEpsilon &&
      relativeChange(current.totalPowerConsumed, previous.totalPowerConsumed) < metricEpsilon;
    if (stable) return true;
  }

  return false;
}

/**
 * Build the human-readable status message for a settled steady-state run.
 * Centralised so the store action and any UI surface format it identically.
 */
export function steadyStateReachedMessage(iterations: number, residual: number): string {
  const res = Number.isFinite(residual) ? residual.toExponential(2) : 'n/a';
  return `Steady state reached (${iterations} iteration${iterations === 1 ? '' : 's'}, residual ${res})`;
}

/** Build the human-readable status message for a run that hit the iteration cap. */
export function steadyStateNotReachedMessage(iterations: number): string {
  return `Did not reach steady state in ${iterations} iterations`;
}
